/**
 * ConfigPanel Component for UI-Model-1
 *
 * Right panel displaying properties for the currently selected element:
 * - Header configuration (when header section selected)
 * - Container configuration
 * - Field configuration
 * - Widget configuration
 * - Category configuration
 * - Connector configuration
 * - Submodel configuration
 */

import React, { useCallback } from 'react';
import { useUIModelState } from '../../contexts/UIModelStateContext';
import { ContainerAPI, ItemAPI, isFieldAPI, isWidgetAPI, isCategoryAPI, isConnectorAPI, WidgetAPI, FieldAPI, ConnectorAPI, CategoryAPI } from '../../types';
import { HeaderConfigPanel } from './HeaderConfigPanel';
import { YourBuildConfigPanel } from './YourBuildConfigPanel';
import { FooterConfigPanel } from './FooterConfigPanel';
import { NavigationConfigPanel } from './NavigationConfigPanel';
import { GroupConfigPanel } from './GroupConfigPanel';
import { PageConfigPanel } from './PageConfigPanel';
import { WidgetConfigPanel } from './WidgetConfigPanel';
import { ContainerConfigPanel } from './ContainerConfigPanel';
import { FieldConfigPanel } from './FieldConfigPanel';
import { ConnectorConfigPanel } from './ConnectorConfigPanel';
import { CategoryConfigPanel } from './CategoryConfigPanel';
import { SubmodelConfigPanel } from './SubmodelConfigPanel';
import { ConnectorFieldConfig, CONNECTOR_DEFAULT_CONFIG, CategoryFieldConfig, CATEGORY_DEFAULT_CONFIG } from '../../config/fieldDefaults';
import { YOUR_BUILD_DEFAULT_CONFIG, mergeYourBuildWithDefaults, YourBuildWidgetConfig } from '../../config/widgetDefaults';
import { updateContainerInList } from '../../commands/implementations/itemCommands';
import { getItemIcon, getItemName, getItemTypeName } from '../../utils/itemDisplayHelpers';
import Typography from 'Components/DesignSystem/Typography/Typography';
import EmptyState from 'Components/DesignSystem/EmptyState/EmptyState';
import { useSaveQueue } from '../../contexts/SaveQueueContext';
import {CloseIcon} from "../../../../../../assests/GmsIcons";

interface ConfigPanelProps {
  id?: string;
}

// Placeholder for item config (fields, widgets, etc.)
const ItemConfig = ({ item }: { item: ItemAPI }) => {
  let title = 'Item Configuration';
  let details: React.ReactNode = null;

  if (isFieldAPI(item)) {
    title = `Field: ${'name' in item ? item.name : 'Unnamed'}`;
    details = (
      <>
        <p className="tw-text-xs tw-text-ink-secondary">Type: {item.fieldType}</p>
        <p className="tw-text-xs tw-text-ink-secondary">Reusable: {item.isReusable ? 'Yes' : 'No'}</p>
      </>
    );
  } else if (isWidgetAPI(item)) {
    title = `Widget: ${item.widgetType}`;
    details = (
      <>
        <p className="tw-text-xs tw-text-ink-secondary">Widget Type: {item.widgetType}</p>
        <p className="tw-text-xs tw-text-ink-secondary">Reusable: {item.isReusable ? 'Yes' : 'No'}</p>
      </>
    );
  } else if (isCategoryAPI(item)) {
    title = `Category: ${'name' in item ? item.name : 'Unnamed'}`;
    details = (
      <>
        <p className="tw-text-xs tw-text-ink-secondary">Category ID: {item.categoryId}</p>
      </>
    );
  } else if (isConnectorAPI(item)) {
    title = `Connector: ${'name' in item ? item.name : 'Unnamed'}`;
  }

  return (
    <div className="tw-space-y-4">
      <h3 className="tw-text-sm tw-font-semibold tw-text-ink">{title}</h3>
      {details}
      <p className="tw-text-xs tw-text-ink-tertiary tw-italic">
        Configuration editor coming soon...
      </p>
    </div>
  );
};

// No selection message — DS EmptyState, `sm` because it sits inside the panel,
// not on a page. (The old markup's second line was tw-text-grey-300, a DEAD
// class — Tailwind spells its scale `gray` — so that line rendered in the
// inherited body colour, not grey, ever since it was written.)
const NoSelection = () => (
  <EmptyState
    size="sm"
    className="tw-py-8"
    title="No element selected"
    description="Click on a container or item to configure it"
    data-testid="config-panel-no-selection"
  />
);

export const ConfigPanel = ({ id }: ConfigPanelProps) => {
  const baseId = id || 'config-panel';
  const { state, dispatch, getContainer } = useUIModelState();
  const { enqueueSave } = useSaveQueue();
  const {
    selectedContainerId,
    selectedItemId,
    selectedItemType,
    selectedSection,
    selectedGroupId,
    selectedPageId,
    selectedPageGroupId,
    selectedSubmodelId,
    activePageId,
  } = state.editorState;

  // Find selected item
  const findItem = (container: ContainerAPI | null, itemId: string): ItemAPI | null => {
    if (!container) return null;
    const items = container.content?.items || [];
    for (const item of items) {
      if ('id' in item && item.id === itemId) {
        return item as ItemAPI;
      }
    }
    return null;
  };

  const selectedContainer = selectedContainerId ? getContainer(selectedContainerId).element ?? null : null;
  const selectedItem =
    selectedItemId && selectedContainer ? findItem(selectedContainer, selectedItemId) : null;

  // Handle widget configuration changes
  const handleWidgetConfigChange = useCallback(
    (config: Record<string, unknown>) => {
      if (!selectedItem || !isWidgetAPI(selectedItem) || !selectedContainerId || !activePageId) {
        return;
      }

      // yourBuild saves to sharedWidgets, not widget.configuration
      if (selectedItem.widgetType === 'yourBuild') {
        const updatedModel = {
          ...state.model!,
          layout: {
            ...state.model!.layout,
            sharedWidgets: {
              ...state.model!.layout?.sharedWidgets,
              yourBuildWidget: config,
            },
          },
        };
        dispatch({ type: 'SET_MODEL', payload: updatedModel });
        enqueueSave('sharedWidgets', { widgetsData: { yourBuildWidget: config } });
        return;
      }

      // Find the canvas ID for the active page
      const canvasId = Object.keys(state.model?.layout.canvas || {}).find(
        (key) => state.model?.layout.canvas[key].pageId === activePageId
      );

      if (!canvasId) return;

      // Create updated widget with new configuration
      const updatedWidget: WidgetAPI = {
        ...selectedItem,
        configuration: config,
      };

      // For contentBlock, also update the content property
      if (selectedItem.widgetType === 'contentBlock' && 'content' in config) {
        updatedWidget.content = config.content as string;
      }

      const canvas = state.model?.layout.canvas[canvasId];
      if (!canvas) return;

      // Use recursive update to find container at ANY nesting depth
      const updatedContainers = updateContainerInList(
        canvas.containers,
        selectedContainerId,
        (container) => {
          const itemIndex = container.content?.items?.findIndex(
            item => 'id' in item && item.id === selectedItem.id
          );
          if (itemIndex === undefined || itemIndex === -1) return container;

          return {
            ...container,
            content: {
              ...container.content,
              items: [
                ...(container.content?.items || []).slice(0, itemIndex),
                updatedWidget,
                ...(container.content?.items || []).slice(itemIndex + 1),
              ],
            },
          };
        }
      );

      // Update the model state
      dispatch({
        type: 'SET_MODEL',
        payload: {
          ...state.model!,
          layout: {
            ...state.model!.layout,
            canvas: {
              ...state.model!.layout.canvas,
              [canvasId]: {
                ...canvas,
                containers: updatedContainers,
              },
            },
          },
        },
      });

      // Mark canvas as dirty (SET_MODEL doesn't mark internally)
      dispatch({
        type: 'MARK_DIRTY',
        payload: { section: 'canvas', canvasId },
      });
    },
    [selectedItem, selectedContainerId, activePageId, state.model, dispatch, enqueueSave]
  );

  // Handle YourBuild widget config changes when accessed from header mini widget
  const handleYourBuildFromHeaderConfigChange = useCallback(
    (config: YourBuildWidgetConfig) => {
      if (!state.model) return;
      const updatedModel = {
        ...state.model,
        layout: {
          ...state.model.layout,
          sharedWidgets: {
            ...state.model.layout?.sharedWidgets,
            yourBuildWidget: config,
          },
        },
      };
      dispatch({ type: 'SET_MODEL', payload: updatedModel });
      enqueueSave('sharedWidgets', { widgetsData: { yourBuildWidget: config } });
    },
    [state.model, dispatch, enqueueSave]
  );

  // Handle field configuration changes
  const handleFieldConfigChange = useCallback(
    (config: Record<string, unknown>) => {
      if (!selectedItem || !isFieldAPI(selectedItem) || !selectedContainerId || !activePageId) {
        return;
      }

      // Find the canvas ID for the active page
      const canvasId = Object.keys(state.model?.layout.canvas || {}).find(
        (key) => state.model?.layout.canvas[key].pageId === activePageId
      );

      if (!canvasId) return;

      // Create updated field with new configuration
      const updatedField: FieldAPI = {
        ...selectedItem,
        configuration: config,
      };

      const canvas = state.model?.layout.canvas[canvasId];
      if (!canvas) return;

      // Use recursive update to find container at ANY nesting depth
      const updatedContainers = updateContainerInList(
        canvas.containers,
        selectedContainerId,
        (container) => {
          const itemIndex = container.content?.items?.findIndex(
            item => 'id' in item && item.id === selectedItem.id
          );
          if (itemIndex === undefined || itemIndex === -1) return container;

          return {
            ...container,
            content: {
              ...container.content,
              items: [
                ...(container.content?.items || []).slice(0, itemIndex),
                updatedField,
                ...(container.content?.items || []).slice(itemIndex + 1),
              ],
            },
          };
        }
      );

      // Update the model state
      dispatch({
        type: 'SET_MODEL',
        payload: {
          ...state.model!,
          layout: {
            ...state.model!.layout,
            canvas: {
              ...state.model!.layout.canvas,
              [canvasId]: {
                ...canvas,
                containers: updatedContainers,
              },
            },
          },
        },
      });

      // Mark canvas as dirty (SET_MODEL doesn't mark internally)
      dispatch({
        type: 'MARK_DIRTY',
        payload: { section: 'canvas', canvasId },
      });
    },
    [selectedItem, selectedContainerId, activePageId, state.model, dispatch]
  );

  // Handle connector configuration changes
  const handleConnectorConfigChange = useCallback(
    (config: ConnectorFieldConfig) => {
      if (!selectedItem || !isConnectorAPI(selectedItem) || !selectedContainerId || !activePageId) {
        return;
      }

      // Find the canvas ID for the active page
      const canvasId = Object.keys(state.model?.layout.canvas || {}).find(
        (key) => state.model?.layout.canvas[key].pageId === activePageId
      );

      if (!canvasId) return;

      // Create updated connector with new configuration
      const updatedConnector: ConnectorAPI = {
        ...selectedItem,
        configuration: config as unknown as Record<string, unknown>,
      };

      const canvas = state.model?.layout.canvas[canvasId];
      if (!canvas) return;

      // Use recursive update to find container at ANY nesting depth
      const updatedContainers = updateContainerInList(
        canvas.containers,
        selectedContainerId,
        (container) => {
          const itemIndex = container.content?.items?.findIndex(
            item => 'id' in item && item.id === selectedItem.id
          );
          if (itemIndex === undefined || itemIndex === -1) return container;

          return {
            ...container,
            content: {
              ...container.content,
              items: [
                ...(container.content?.items || []).slice(0, itemIndex),
                updatedConnector,
                ...(container.content?.items || []).slice(itemIndex + 1),
              ],
            },
          };
        }
      );

      // Update the model state
      dispatch({
        type: 'SET_MODEL',
        payload: {
          ...state.model!,
          layout: {
            ...state.model!.layout,
            canvas: {
              ...state.model!.layout.canvas,
              [canvasId]: {
                ...canvas,
                containers: updatedContainers,
              },
            },
          },
        },
      });

      // Mark canvas as dirty (SET_MODEL doesn't mark internally)
      dispatch({
        type: 'MARK_DIRTY',
        payload: { section: 'canvas', canvasId },
      });
    },
    [selectedItem, selectedContainerId, activePageId, state.model, dispatch]
  );

  // Handle category configuration changes
  const handleCategoryConfigChange = useCallback(
    (config: CategoryFieldConfig) => {
      if (!selectedItem || !isCategoryAPI(selectedItem) || !selectedContainerId || !activePageId) {
        return;
      }

      // Find the canvas ID for the active page
      const canvasId = Object.keys(state.model?.layout.canvas || {}).find(
        (key) => state.model?.layout.canvas[key].pageId === activePageId
      );

      if (!canvasId) return;

      // Create updated category with new configuration
      const updatedCategory: CategoryAPI = {
        ...selectedItem,
        configuration: config as unknown as Record<string, unknown>,
      };

      const canvas = state.model?.layout.canvas[canvasId];
      if (!canvas) return;

      // Use recursive update to find container at ANY nesting depth
      const updatedContainers = updateContainerInList(
        canvas.containers,
        selectedContainerId,
        (container) => {
          const itemIndex = container.content?.items?.findIndex(
            item => 'id' in item && item.id === selectedItem.id
          );
          if (itemIndex === undefined || itemIndex === -1) return container;

          return {
            ...container,
            content: {
              ...container.content,
              items: [
                ...(container.content?.items || []).slice(0, itemIndex),
                updatedCategory,
                ...(container.content?.items || []).slice(itemIndex + 1),
              ],
            },
          };
        }
      );

      // Update the model state
      dispatch({
        type: 'SET_MODEL',
        payload: {
          ...state.model!,
          layout: {
            ...state.model!.layout,
            canvas: {
              ...state.model!.layout.canvas,
              [canvasId]: {
                ...canvas,
                containers: updatedContainers,
              },
            },
          },
        },
      });

      // Mark canvas as dirty (SET_MODEL doesn't mark internally)
      dispatch({
        type: 'MARK_DIRTY',
        payload: { section: 'canvas', canvasId },
      });
    },
    [selectedItem, selectedContainerId, activePageId, state.model, dispatch]
  );

  // Determine what to render
  let content: React.ReactNode;
  let panelTitle = 'Properties';

  if (selectedSection === 'header') {
    // Check for section selection (header, footer, navigation)
    content = <HeaderConfigPanel id={`${baseId}-header`} />;
    panelTitle = ''; // HeaderConfigPanel has its own "Header" row with toggle
  } else if (selectedSection === 'header-your-build') {
    // YourBuild widget config accessed via mini widget click in header preview
    const sharedConfig = state.model?.layout?.sharedWidgets?.yourBuildWidget;
    const resolvedConfig = mergeYourBuildWithDefaults({
      ...YOUR_BUILD_DEFAULT_CONFIG,
      ...(sharedConfig || {}),
    });
    panelTitle = '';
    content = (
      <div id={`${baseId}-your-build-panel`}>
        <div className="tw-flex tw-items-center tw-justify-between tw-pb-3 tw-border-b tw-border-edge">
          <h3 className="tw-text-sm tw-font-semibold tw-text-ink">Your Build Widget</h3>
          <button aria-label="Close"
            id={`${baseId}-your-build-close-btn`}
            data-testid={`${baseId}-your-build-close-btn`}
            onClick={() => dispatch({ type: 'SET_SELECTED_SECTION', payload: { section: 'header' } })}
            className="tw-text-ink-secondary hover:tw-text-ink [&_path]:tw-stroke-current"
          >
            <CloseIcon />
          </button>
        </div>
        <YourBuildConfigPanel
          id={`${baseId}-your-build-config`}
          config={resolvedConfig}
          onChange={handleYourBuildFromHeaderConfigChange}
          isFromHeader
        />
      </div>
    );
  } else if (selectedSection === 'footer') {
    content = <FooterConfigPanel id={`${baseId}-footer`} />;
    panelTitle = ''; // FooterConfigPanel has its own "Footer" row with toggle
  } else if (selectedSection === 'navigation') {
    content = <NavigationConfigPanel id={`${baseId}-navigation`} />;
    panelTitle = ''; // NavigationConfigPanel has its own "Navigation" row with toggle
  } else if (selectedGroupId && !selectedPageId) {
    // Group selected (but not a page)
    content = <GroupConfigPanel id={`${baseId}-group`} groupId={selectedGroupId} />;
    panelTitle = 'Group';
  } else if (selectedPageId && selectedPageGroupId) {
    // Page selected
    content = <PageConfigPanel id={`${baseId}-page`} pageId={selectedPageId} groupId={selectedPageGroupId} />;
    panelTitle = 'Page';
  } else if (selectedSubmodelId && selectedContainerId) {
    // Submodel selected
    panelTitle = '';
    content = (
      <SubmodelConfigPanel
        submodelId={selectedSubmodelId}
        containerId={selectedContainerId}
      />
    );
  } else if (selectedItem && selectedItemType) {
    // Use FieldConfigPanel for fields, WidgetConfigPanel for widgets, ConnectorConfigPanel for connectors
    // panelTitle is empty - unified header is rendered separately for items
    panelTitle = '';
    if (isFieldAPI(selectedItem)) {
      content = (
        <FieldConfigPanel
          fieldType={selectedItem.fieldType.toLowerCase()}
          config={selectedItem.configuration || {}}
          onChange={handleFieldConfigChange}
        />
      );
    } else if (isWidgetAPI(selectedItem)) {
      content = (
        <WidgetConfigPanel
          widget={selectedItem}
          onConfigChange={handleWidgetConfigChange}
        />
      );
    } else if (isConnectorAPI(selectedItem)) {
      // Build connector config from stored configuration with defaults
      const connectorConfig: ConnectorFieldConfig = {
        ...CONNECTOR_DEFAULT_CONFIG,
        label: selectedItem.name || 'Connector',
        displayName: selectedItem.name || 'Connector',
        ...(selectedItem.configuration as Partial<ConnectorFieldConfig> || {}),
      };
      content = (
        <ConnectorConfigPanel
          config={connectorConfig}
          onChange={handleConnectorConfigChange}
        />
      );
    } else if (isCategoryAPI(selectedItem)) {
      // Build category config from stored configuration with defaults
      const categoryConfig: CategoryFieldConfig = {
        ...CATEGORY_DEFAULT_CONFIG,
        label: selectedItem.name || 'Category',
        ...(selectedItem.configuration as Partial<CategoryFieldConfig> || {}),
      };
      content = (
        <CategoryConfigPanel
          config={categoryConfig}
          onChange={handleCategoryConfigChange}
          categoryId={selectedItem.categoryId}
        />
      );
    } else {
      content = <ItemConfig item={selectedItem} />;
    }
  } else if (selectedContainer) {
    content = <ContainerConfigPanel container={selectedContainer} />;
  } else {
    content = <NoSelection />;
  }

  return (
    <div
      id={baseId}
      data-testid={baseId}
      className="tw-w-80 tw-h-full tw-bg-surface tw-border-l tw-border-edge tw-overflow-y-auto tw-flex-shrink-0"
    >
      {/* Standard panel title for non-item selections */}
      {panelTitle && (
        <div className="tw-px-4 tw-py-3 tw-border-b tw-border-edge">
          <h2 className="tw-text-sm tw-font-semibold tw-text-ink">{panelTitle}</h2>
        </div>
      )}
      {/* Unified header for items (fields, widgets, connectors, categories) */}
      {selectedItem && selectedItemType && (
        <div className="tw-px-4 tw-py-3 tw-border-b tw-border-edge">
          <div className="tw-flex tw-items-center tw-justify-between">
            <div className="tw-flex tw-items-center tw-gap-2">
              <span className="tw-text-ink-secondary">{getItemIcon(selectedItem)}</span>
              <Typography variant="title-header" className="tw-text-ink">
                {getItemName(selectedItem)}
              </Typography>
            </div>
            <span className="tw-text-[10px] tw-px-2 tw-py-0.5 tw-bg-tone-neutral-tint tw-text-tone-neutral-ink tw-rounded-full tw-uppercase tw-font-medium">
              {getItemTypeName(selectedItem)}
            </span>
          </div>
        </div>
      )}
      <div className="tw-p-4">{content}</div>
    </div>
  );
};

export default ConfigPanel;
